import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createConsumer } from '@rails/actioncable';

import { SERVER_URL } from '../environment';
import { setNewMessageState } from '../actions/misc';
import { fetchTodayAppointments } from '../ducks/dashboard/todayAppointments';
import { fetchAppointment } from '../actions/sessions';

const NotificationManager = ({
  location,
  authenticated,
  setNewMessageState,
  fetchTodayAppointments,
  fetchAppointment,
  newMessageHandler = null,
}) => {
  const cableRef = useRef(null);
  const channelRef = useRef(null);
  const propsRef = useRef({
    location,
    newMessageHandler,
    setNewMessageState,
    fetchTodayAppointments,
    fetchAppointment,
  });

  propsRef.current = {
    location,
    newMessageHandler,
    setNewMessageState,
    fetchTodayAppointments,
    fetchAppointment,
  };

  const unsubscribe = () => {
    if (channelRef.current) {
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }

    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }
  };

  const handleReceived = notification => {
    const {
      location: currentLocation,
      newMessageHandler: currentHandler,
      setNewMessageState: setMessageState,
      fetchTodayAppointments: fetchToday,
      fetchAppointment: fetchAppt,
    } = propsRef.current;
    const currentPath = currentLocation.pathname;
    if (notification.type === 'new_chat_message') {
      if (/^\/messages/.test(currentPath)) {
        if (currentHandler) {
          currentHandler(notification);
        }
      } else {
        setMessageState(true);
      }
    } else if (notification.type === 'reschedule_appointment') {
      if (/^\/dashboard/.test(currentPath) || currentPath === '/') {
        fetchToday();
      }
    } else if (notification.type === 'audio_conference') {
      if (
        /^\/new_video.+$/.test(currentPath) ||
        /^\/sessions.+video_call$/.test(currentPath)
      ) {
        if (notification.body && notification.body.appointment_id) {
          fetchAppt(notification.body.appointment_id);
        }
      }
    }
  };

  const subscribe = () => {
    const token = localStorage.getItem('token');

    if (token) {
      cableRef.current = createConsumer(`${SERVER_URL}/cable?token=${token}`);
      channelRef.current = cableRef.current.subscriptions.create(
        { channel: 'WebNotificationChannel' },
        {
          received: handleReceived,
          initialized: () => {},
          connected: () => {},
          rejected: () => {
            if (cableRef.current) cableRef.current.disconnect();
          },
          disconnected: () => {},
        },
      );
    }
  };

  useEffect(() => {
    if (authenticated) {
      subscribe();
    } else {
      unsubscribe();
    }
    // Match class DidMount/DidUpdate: only react to authenticated changes.
    // Class had no WillUnmount cleanup — leave subscription if still authenticated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  return <noscript />;
};

NotificationManager.propTypes = {
  location: PropTypes.object.isRequired,
  authenticated: PropTypes.bool.isRequired,
  setNewMessageState: PropTypes.func.isRequired,
  fetchTodayAppointments: PropTypes.func.isRequired,
  fetchAppointment: PropTypes.func.isRequired,
  newMessageHandler: PropTypes.func,
};

function mapStateToProps(state) {
  const { authenticated } = state.auth;
  const { newMessageHandler } = state.misc;

  return {
    authenticated,
    newMessageHandler,
  };
}

export default withRouter(
  connect(mapStateToProps, {
    setNewMessageState,
    fetchTodayAppointments,
    fetchAppointment,
  })(NotificationManager),
);
